import { Trans } from 'react-i18next';
import AddButton from '../../component/button/button';
import { chunk, isArray, isString } from 'lodash';
import { countries, renderFormRow } from '../../component/userModal/helper';
import { FieldConfig } from '../../strategies/userPresentation/userPresentationStrategy.interface';
import Input from '../../component/Input/input';
import { ChangeEvent } from 'react';
import DropDownInput from '../../component/dropDownInput/dropDownInput';
import {
  CountryProps,
  Option,
} from '../../component/dropDownInput/dropDownInput.type';
import CancelButton from '../../component/button/button';
import ButtonSpinner from '../../assets/buttonLoading/buttonLoading';
import ResetButton from '../../component/button/button';
import { renderChangePasswordInput } from '../login/loginHelper';
import { LoginTranslation } from '../../utils/helper.type';
/**
 * Renders the header section for the user profile page.
 *
 * The header consists of:
 * - A title displaying the localized text for "My Account".
 * - An "Edit" button to trigger editing of profile details.
 *
 * Layout uses flexbox to space the title and button apart.
 * Styling includes font weight, size, and colors for the title,
 * and padding, rounded corners, and font size for the button.
 */
export const renderProfileHeader = (
  handleEdit: () => void,
  showEdit: boolean,
  handleCancel: () => void,
  handleSave?: () => void,
  updateLoading?: boolean,
) => {
  return (
    <div className="justify-between flex">
      <div className="text-[#1A1A1A] text-base font-semibold">
        {' '}
        <Trans i18nKey="userManagement.myAccount" />
      </div>
      {showEdit ? (
        <div>
          <CancelButton
            className="w-28 py-2 rounded-full bg-white border text-[#1A1A1A]  hover:text-[#1A1A1A] text-sm border-[#D8D8D8] hover:bg-white"
            onClick={handleCancel}
          >
            <Trans i18nKey="userManagement.cancel" />
          </CancelButton>
          <AddButton
            onClick={handleSave}
            className="px-12 py-2 rounded-full text-sm ml-5"
          >
            {updateLoading ? (
              <ButtonSpinner />
            ) : (
              <Trans i18nKey="profile.save" />
            )}
          </AddButton>
        </div>
      ) : (
        <AddButton
          onClick={handleEdit}
          className="px-12 py-2 rounded-full text-sm"
        >
          <Trans i18nKey="userManagement.edit" />
        </AddButton>
      )}
    </div>
  );
};

/**
 * Renders a row displaying two profile detail fields side by side.
 *
 * Each field consists of a label and its corresponding value:
 * - Labels are localized using the `Trans` component via the given i18n keys (`label1` and `label2`).
 * - Values are displayed as plain text.
 *
 * Layout and styling details:
 * - The row is a flex container with space between the two fields and some margin on top.
 * - Each field takes half the row width (`w-1/2`) and contains:
 *   - A label section with 40% width, bold font, small size, dark text color, and minimum height.
 *   - A value section with 60% width, normal font weight, slightly lighter text color, and minimum height.
 * - Horizontal gap (`gap-6`) between the two fields to keep spacing consistent.
 *
 * This component is useful for displaying pairs of profile information neatly aligned in two columns.
 *
 * @param label1 - The i18n key for the first field's label.
 * @param value1 - The value text for the first field.
 * @param label2 - The i18n key for the second field's label.
 * @param value2 - The value text for the second field.
 * @returns A JSX element representing a profile detail row.
 */
export const renderProfileViewRow = (
  label1: string,
  value1: string,
  label2: string,
  value2: string,
): JSX.Element => (
  <div className="grid grid-cols-4 gap-y-2 gap-x-8 mt-4">
    {/* First profile column */}
    <div className="flex">
      <div className="w-32 font-semibold text-sm text-[#1A1A1A] min-h-[30px]">
        <Trans i18nKey={label1} />
      </div>
      <div className="text-sm text-[#1F1F1F] min-h-[20px]">{value1}</div>
    </div>

    {/* Second profile column */}
    <div className="flex">
      <div className="w-32 font-semibold text-sm text-[#1A1A1A] min-h-[20px]">
        <Trans i18nKey={label2} />
      </div>
      <div className="text-sm text-[#1F1F1F] min-h-[20px]">{value2}</div>
    </div>
  </div>
);

/**
 * Renders the detailed view of a user profile by displaying user details in rows.
 *
 * The user details are passed as an object with string keys and string values.
 * This function:
 * - Converts the userDetails object into an array of `[label, value]` tuples.
 * - Splits this array into chunks of two pairs each, to display two details per row.
 * - Maps over these pairs, rendering each row using `renderProfileViewRow`.
 *
 * @param userDetails - An object representing user profile fields and their string values.
 * @returns JSX element containing the profile detail rows.
 */
export const renderViewProfileDetail = (
  userDetails: Record<string, string>,
): JSX.Element => {
  // Extract entries from profile details: [fieldLabel, fieldValue]
  const profileDetailLabelValue: [string, string][] =
    Object.entries(userDetails);
  // Group the profile entries into pairs for display or layout purposes
  const profileDetailLayout: [string, string][][] = chunk(
    profileDetailLabelValue,
    2,
  );

  return (
    <>
      {profileDetailLayout.map(
        (profileDetailLabelValue: [string, string][], index: number) => {
          const [label1, value1] = profileDetailLabelValue[0];
          const [label2, value2] = profileDetailLabelValue[1] || ['', ''];

          return renderProfileViewRow(label1, value1, label2, value2);
        },
      )}
    </>
  );
};

/**
 * Renders the user's profile picture section with a title and image.
 *
 * @param imageUrl - The URL of the profile image, typically fetched from an API.
 * @returns JSX element showing the profile picture.
 */
export const renderProfilePictureView = (profileImage: string): JSX.Element => {
  return (
    <>
      <div className="text-[#1A1A1A] font-semibold pt-4">
        {' '}
        <Trans i18nKey="profile.profilePicture" />
      </div>
      <div className="flex flex-col py-4">
        <img
          src={profileImage}
          alt="Profile"
          className="w-[120px] h-[120px] rounded-full object-cover"
        />
      </div>
    </>
  );
};

export const renderSignatureView = (signatureImage: string): JSX.Element => {
  return (
    <>
      <div className="text-[#1A1A1A] font-semibold pt-4">
        <Trans i18nKey="onboarding.signature" />
      </div>
      <div className="flex flex-col py-4">
        <div className="w-40 h-40 border rounded bg-white flex items-center justify-center">
          <img
            src={signatureImage}
            alt="Signature"
            className="max-w-full max-h-full object-contain"
          />
        </div>
      </div>
    </>
  );
};

/**
 * Renders a form field based on the provided configuration
 *
 * @param config - The field configuration
 * @param value - The current value of the field
 * @param onChange - Function to call when the value changes
 * @param errorMessage - Optional error message to display
 * @returns A JSX element representing the form field
 */
export const renderProfileFormField = (
  config: FieldConfig,
  value: any, //TODO I will look later this type
  onChange: (value: any) => void,
  errorMessage?: string,
) => {
  const renderField = (): JSX.Element => {
    switch (config.type) {
      case 'text':
      case 'email':
        return (
          <Input
            placeholder={config.placeholder}
            value={value || ''}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onChange(event.target.value)
            }
            errorMessage={errorMessage}
            disabled={config.disabled}
          />
        );

      case 'dropdown':
        return (
          <DropDownInput
            placeholder={config.placeholder}
            options={config.options || []}
            selected={value}
            onSelect={(value: string | string[]) => {
              if (isString(value)) {
                onChange(value);
              }
            }}
            errorMessage={errorMessage}
          />
        );

      case 'phone':
        return (
          <div className="flex w-full gap-2">
            <div className="w-[30%]">
              <DropDownInput
                isCountry
                countries={countries}
                selectedCountry={value?.country || countries[0]}
                onSelectCountry={(country: CountryProps) => {
                  onChange({ ...value, country });
                }}
              />
            </div>
            <div className="w-[70%]">
              <Input
                value={value?.number === 0 ? '' : value?.number || ''}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const inputValue = event.target.value;

                  onChange({
                    ...value,
                    number:
                      inputValue === '' ? undefined : parseInt(inputValue, 10),
                  });
                }}
                placeholder={config.placeholder}
                errorMessage={errorMessage}
                disabled={config.disabled}
              />
            </div>
          </div>
        );

      case 'readonly':
        let displayValue: string = config.defaultValue || '';

        // If readonly field is a dropdown or multiselect, get the label from options
        if (config.options) {
          // if value is an array, the field was multiselect, so we must get all labels
          if (Array.isArray(value)) {
            displayValue = config.options
              .filter((opt: Option) => value.includes(opt.value))
              .map((opt: Option) => opt.label)
              .join(', ');
          } else if (typeof value === 'string') {
            displayValue =
              config.options.find((opt: Option) => opt.value === value)
                ?.label || '';
          }
        }
        return (
          <Input value={displayValue} disabled={true} onChange={() => {}} />
        );

      case 'multiselect':
        return (
          <DropDownInput
            placeholder={config.placeholder}
            isMulti
            options={config.options || []}
            selected={value || []}
            onSelect={(value: string | string[]) => {
              if (isArray(value)) {
                onChange(value);
              }
            }}
            errorMessage={errorMessage}
          />
        );

      default:
        const error: string = `Unsupported field type: ${config.type}`;
        console.error(error);
        throw new Error(error);
    }
  };

  return renderFormRow(<Trans i18nKey={config.label} />, renderField());
};

export const renderProfileCreationForm = (
  visibleFields: FieldConfig[],
  formValues: Record<string, string>,
  handleChange: (field: string, value: string) => void,
  errorMessage: Record<string, string>,
) => {
  const fieldChunks = chunk(visibleFields, 2);

  return (
    <div>
      {fieldChunks.map((pair, rowIndex) => (
        <div key={`row-${rowIndex}`} className="flex gap-16 mb-4">
          {pair.map((config) => (
            <div key={config.id} className="w-[35%]">
              {renderProfileFormField(
                config,
                formValues[config.id],
                (value) => handleChange(config.id, value),
                errorMessage[config.id],
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export const renderChangePasswordView = (
  handleChangePassword: () => void,
): JSX.Element => {
  return (
    <div className="mt-8">
      <hr />
      <div className="mt-5 mb-3 font-semibold text-base text-[#1A1A1A]">
        <Trans i18nKey="login.password" />
      </div>
      <div className="text-sm text-[#1F1F1F] ">
        <Trans i18nKey="setPassword.resetYourPassword" />
      </div>
      <ResetButton
        onClick={handleChangePassword}
        className="px-8 py-2 rounded-full text-sm mt-5 mb-8"
      >
        <Trans i18nKey="setPassword.resetPassword" />
      </ResetButton>
      <hr />
    </div>
  );
};

export const renderChangePasswordInputView = (
  handleSubmit: (e: React.FormEvent) => void,
  currentPassword: string,
  setCurrentPassword: React.Dispatch<React.SetStateAction<string>>,
  newPassword: string,
  setNewPassword: React.Dispatch<React.SetStateAction<string>>,
  confirmPassword: string,
  setConfirmPassword: React.Dispatch<React.SetStateAction<string>>,
  loginTranslation: LoginTranslation,
  showCurrentPassword: boolean,
  setShowCurrentPassword: React.Dispatch<React.SetStateAction<boolean>>,
  showNewPassword: boolean,
  setShowNewPassword: React.Dispatch<React.SetStateAction<boolean>>,
  showConfirmPassword: boolean,
  setShowConfirmPassword: React.Dispatch<React.SetStateAction<boolean>>,
  error: string | null,
) => {
  return (
    <div className="flex flex-col flex-1 px-10 py-6">
      <div>
        <form onSubmit={handleSubmit}>
          {renderChangePasswordInput(
            <Trans i18nKey="login.currentPassword" />,
            currentPassword,
            setCurrentPassword,
            loginTranslation.password,
            showCurrentPassword,
            setShowCurrentPassword,
          )}
          {renderChangePasswordInput(
            <Trans i18nKey="login.newPassword" />,
            newPassword,
            setNewPassword,
            loginTranslation.password,
            showNewPassword,
            setShowNewPassword,
            !!error,
          )}
          {renderChangePasswordInput(
            <Trans i18nKey="login.confirmNewPassword" />,
            confirmPassword,
            setConfirmPassword,
            loginTranslation.password,
            showConfirmPassword,
            setShowConfirmPassword,
            !!error,
          )}

          {error && <p className="text-base text-[#D80000] mt-2">{error}</p>}
        </form>
      </div>
    </div>
  );
};
